'use client';

import { Input } from '@packages/shared/src/ui/Input/Input';
import { Card, CardHeader, CardContent } from '@packages/shared/src/ui/Card/Card';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@packages/shared/src/ui/ToggleButtonGroup/ToggleButtonGroup';
import type { SampleFormSchema } from '../schema';
import { PrimaryButton } from '../../components/PrimaryButton/PrimaryButton.client';
import { SecondaryButton } from '../../components/SecondaryButton/SecondaryButton.client';
import { SampleOrderContent } from '../../components/SampleOrderContent/SampleOrderContent';
import type { SetStep } from '../../types';
import { SampleOrderSteps } from '../types';
import { ActionButtonsWrapper } from '../../components/ActionButtonsWrapper/ActionButtonsWrapper';
import { sharedMessages } from '../shared.messages';

export type PersonalDataFormProps = SetStep;

export const PersonalDataForm = ({ setStep }: PersonalDataFormProps) => {
  const { formatMessage } = useIntl();

  const {
    formState: { isValid, errors, defaultValues },
    control,
    register,
  } = useFormContext<SampleFormSchema>();

  return (
    <SampleOrderContent
      pageContent={
        <Card>
          <CardHeader hasDivider className="px-2 py-1.5">
            {formatMessage(sharedMessages.deliveryAddress)}
          </CardHeader>
          <CardContent className="p-2">
            <div className="flex flex-col items-stretch justify-stretch gap-2">
              {/* salutation */}
              <div className="w-full">
                <Controller
                  control={control}
                  name="inputData.salutation"
                  render={({ field: { onChange, value } }) => (
                    <ToggleGroup
                      id="salutation"
                      type="single"
                      value={value}
                      onValueChange={onChange}
                      defaultValue={defaultValues?.inputData?.salutation}
                    >
                      <ToggleGroupItem value="Ms">
                        {formatMessage(sharedMessages.ms)}
                      </ToggleGroupItem>
                      <ToggleGroupItem value="Mr">
                        {formatMessage(sharedMessages.mr)}
                      </ToggleGroupItem>
                    </ToggleGroup>
                  )}
                />
              </div>
              {/* name */}
              <div className="w-full">
                <Input
                  {...register('inputData.name')}
                  className="w-full"
                  label={formatMessage({
                    defaultMessage: 'Vorname',
                    id: 'sampleOrder.personalDataForm.labels.name',
                  })}
                  required
                  helperText={
                    errors.inputData?.name &&
                    formatMessage({
                      defaultMessage: 'Bitte min. 2 Zeichen eingeben.',
                      id: 'sampleOrder.personalDataForm.labels.name.error.tooShort',
                    })
                  }
                  error={!!errors.inputData?.name}
                />
              </div>
              {/* surname */}
              <div className="w-full">
                <Input
                  {...register('inputData.surname')}
                  className="w-full"
                  label={formatMessage({
                    defaultMessage: 'Nachname',
                    id: 'sampleOrder.personalDataForm.labels.surname',
                  })}
                  required
                  helperText={
                    errors.inputData?.surname &&
                    formatMessage({
                      defaultMessage: 'Bitte min. 2 Zeichen eingeben.',
                      id: 'sampleOrder.personalDataForm.labels.surname.error.tooShort',
                    })
                  }
                  error={!!errors.inputData?.surname}
                />
              </div>
              {/* street & house number */}
              <div className="flex w-full flex-col flex-nowrap gap-2 lg:flex-row">
                <div className="w-full lg:w-9/12">
                  <Input
                    {...register('inputData.street')}
                    className="w-full"
                    label={formatMessage({
                      defaultMessage: 'Straße',
                      id: 'sampleOrder.personalDataForm.labels.street',
                    })}
                    required
                    helperText={
                      errors.inputData?.street &&
                      formatMessage({
                        defaultMessage: 'Bitte min. 3 Zeichen eingeben.',
                        id: 'sampleOrder.personalDataForm.labels.street.error.tooShort',
                      })
                    }
                    error={!!errors.inputData?.street}
                  />
                </div>
                <div className="w-full lg:w-3/12">
                  <Input
                    {...register('inputData.houseNumber')}
                    className="w-full"
                    label={formatMessage({
                      defaultMessage: 'Hausnr.',
                      id: 'sampleOrder.personalDataForm.labels.houseNumber',
                    })}
                    required
                    helperText={
                      errors.inputData?.houseNumber &&
                      formatMessage({
                        defaultMessage: 'Bitte min. 1 Zeichen eingeben.',
                        id: 'sampleOrder.personalDataForm.labels.houseNumber.error.tooShort',
                      })
                    }
                    error={!!errors.inputData?.houseNumber}
                  />
                </div>
              </div>
              {/* zipcode & location */}
              <div className="flex w-full flex-col flex-nowrap gap-2 lg:flex-row">
                <div className="w-full lg:w-3/12">
                  <Input
                    {...register('inputData.postalCode')}
                    className="w-full"
                    label={formatMessage({
                      defaultMessage: 'Postleitzahl',
                      id: 'sampleOrder.personalDataForm.labels.postalCode',
                    })}
                    required
                    helperText={
                      errors.inputData?.postalCode &&
                      formatMessage({
                        defaultMessage: 'Bitte min. 3 Zeichen eingeben.',
                        id: 'sampleOrder.personalDataForm.labels.postalCode.error.tooShort',
                      })
                    }
                    error={!!errors.inputData?.postalCode}
                  />
                </div>
                <div className="w-full lg:w-9/12">
                  <Input
                    {...register('inputData.location')}
                    className="w-full"
                    label={formatMessage({
                      defaultMessage: 'Ort',
                      id: 'sampleOrder.personalDataForm.labels.location',
                    })}
                    required
                    helperText={
                      errors.inputData?.location &&
                      formatMessage({
                        defaultMessage: 'Bitte min. 3 Zeichen eingeben.',
                        id: 'sampleOrder.personalDataForm.labels.location.error.tooShort',
                      })
                    }
                    error={!!errors.inputData?.location}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      }
      actionBarContent={
        <ActionButtonsWrapper
          primary={
            <PrimaryButton
              disabled={!isValid}
              actionHandler={() => {
                setStep(SampleOrderSteps.Review);
              }}
              text={formatMessage({
                defaultMessage: 'An diese Adresse liefern',
                id: 'sampleOrder.personalDataForm.button.next',
              })}
              disabledText={formatMessage({
                defaultMessage: 'Bitte gib eine Lieferadresse an',
                id: 'sampleOrder.personalDataForm.button.disabled',
              })}
            />
          }
          secondary={
            <SecondaryButton
              actionHandler={() => {
                setStep(SampleOrderSteps.SelectSamples);
              }}
              text={formatMessage({
                defaultMessage: 'Zurück zur Musterauswahl',
                id: 'sampleOrder.personalDataForm.button.back',
              })}
            />
          }
        />
      }
    />
  );
};
